import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BookingCriterionApply } from '../../model/booking-criterion-apply.model';
import { environment } from '../../../environments/environment';

interface BookingCriterionApplyApiModel {
  booking_CriterionApply_ID: string;
  booking_Rule_ID: string;
  element_ID: string | null;
  number: number;
  value: string | null;
  element_Name?: string | null;
}

interface BookingCriterionApplyApiPayload {
  booking_CriterionApply_ID: string;
  booking_Rule_ID: string;
  element_ID: string | null;
  number: number;
  value: string | null;
  element_Name: string;
  changeUser: string | null;
  changeDateTime: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class BookingCriterionApplyService {
  private apiUrl =
    environment.apiUrl + '/kbj-frontend/api/v1/BookingRule/BookingCriterionApply';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all criterion-apply records for the given booking rule.
   * Returns an empty array when the API responds with 404 or 204 No Content.
   */
  getByContext(bookingRuleId: string): Observable<BookingCriterionApply[]> {
    return this.http
      .get<BookingCriterionApplyApiModel[] | null>(
        this.apiUrl + '/ByContext/' + bookingRuleId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiModelToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new criterion-apply record. The id is forced to the empty guid so
   * the backend can generate a new one.
   */
  addBookingCriterionApply(model: BookingCriterionApply): Observable<unknown> {
    const payload = this.mapModelToApiPayload({ ...model, bookingCriterionApplyId: EMPTY_GUID });

    return this.http
      .post<unknown>(this.apiUrl, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Updates an existing criterion-apply record.
   */
  updateBookingCriterionApply(id: string, model: BookingCriterionApply): Observable<unknown> {
    const payload = this.mapModelToApiPayload({ ...model, bookingCriterionApplyId: id });

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Removes a criterion-apply record by its id.
   */
  removeBookingCriterionApply(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          // Deleting an already-removed apply is a no-op success, so a retry after a
          // partial save stays idempotent.
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  // ------------------------------------------------------------------------------

  private mapApiModelToModel(apiModel: BookingCriterionApplyApiModel): BookingCriterionApply {
    return {
      bookingCriterionApplyId: apiModel.booking_CriterionApply_ID,
      bookingRuleId: apiModel.booking_Rule_ID,
      elementId: apiModel.element_ID,
      number: apiModel.number,
      value: apiModel.value,
      elementName: apiModel.element_Name ?? '',
    };
  }

  private mapModelToApiPayload(model: BookingCriterionApply): BookingCriterionApplyApiPayload {
    return {
      booking_CriterionApply_ID: model.bookingCriterionApplyId ?? EMPTY_GUID,
      booking_Rule_ID: model.bookingRuleId,
      element_ID: model.elementId,
      number: model.number,
      value: model.value,
      element_Name: model.elementName ?? '',
      changeUser: null,
      changeDateTime: new Date().toISOString(),
    };
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
